import { FC, Fragment, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Card } from 'antd'
import PerText from '../../../components/custom_components/Text'
import { SelfreflectionQuestionBoolean, Meeting, SelfreflectionQuestion } from '../../../types'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerIcon from '../../../components/custom_components/Icon'
import { icons } from '../../../components/custom_components/Icon/icons'
import { Colors } from '../../../components/util/types'
import { isDefined } from '../../../types.helper'

interface MeetingStatusModalSelfReflectionProps {
  meeting?: Meeting
  selfReflectionQuestions?: SelfreflectionQuestion[]
  userIsHR: boolean
}

const Tile = ({ header, wide, children }: { header: ReactNode; wide?: number; children: ReactNode }) => {
  return (
    <Col span={wide ? wide : 8} style={{ marginBottom: '12px' }}>
      <Card bordered={false} className="fullHeight">
        <PerFlexRow className="fullHeight noWrap" direction="column" align="middle">
          <PerText mb="s" className="fullWidth">
            {header}
          </PerText>
          {children}
        </PerFlexRow>
      </Card>
    </Col>
  )
}

export const MeetingStatusModalSelfReflection: FC<MeetingStatusModalSelfReflectionProps> = ({
  meeting,
  selfReflectionQuestions,
  userIsHR,
}) => {
  const [t] = useTranslation()

  if (!meeting || !selfReflectionQuestions) {
    return null
  }

  // Components

  const valueClass = 'per-flexRow--fixed'
  const inlineIconSize = 18

  const ValueRow = ({ justify, className, children }: { justify?: boolean; className?: string; children: ReactNode }) => {
    const flexClass = ' fullHeight fullWidth noWrap'
    const flexAlign = 'middle'
    return (
      <>
        {justify ? (
          <PerFlexRow align={flexAlign} justify="center" className={className + flexClass}>
            {children}
          </PerFlexRow>
        ) : (
          <PerFlexRow align={flexAlign} className={className + flexClass}>
            {children}
          </PerFlexRow>
        )}
      </>
    )
  }

  const ValueIcon = ({ name, color }: { name: keyof typeof icons; color?: Colors }) => {
    return <PerIcon name={name} color={color} size={50} className={valueClass} />
  }

  const ValueComment = ({ comment, noMargin }: { comment: string; noMargin?: boolean }) => {
    return (
      <>
        {comment && (
          <PerText appearance="quote" ml={noMargin ? undefined : 'm'} className="per-flexRow--growShrink">
            "{comment}"
          </PerText>
        )}
      </>
    )
  }

  const NotPrepared = () => {
    return (
      <ValueRow justify={false}>
        <PerIcon name="warning_fill" size={inlineIconSize} color="warning" className={valueClass} />
        <PerText appearance="quote" ml="xxxs" className="per-flexRow-growShrink">
          {meeting.employee.legalFirstName + ' ' + t('appraisalMeeting.conclusion.selfReflection.notPrepared')}
        </PerText>
      </ValueRow>
    )
  }

  const NotShared = () => {
    return (
      <ValueRow justify={false}>
        <PerIcon name="warning_fill" size={inlineIconSize} className={valueClass} />
        <PerText appearance="quote" ml="xxxs" className="per-flexRow-growShrink">
          {meeting.employee.legalFirstName + ' ' + t('appraisalMeeting.conclusion.selfReflection.notShared')}
        </PerText>
      </ValueRow>
    )
  }

  // Render Functions

  const renderNumber = (value: number | undefined | null, comment: string | null | undefined, index: number) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sharedInformation = meeting.appraisal!.selfReflectionShared
    return (
      <Fragment key={'number-' + index}>
        {meeting.employeePrepared &&
        !sharedInformation &&
        (!meeting.managerPrepared || !meeting.selfReflectionAndEvaluationVisible) &&
        !userIsHR ? (
          <NotShared />
        ) : meeting.employeePrepared && (value || value === 0 || comment) ? (
          <ValueRow>
            {(value || value === 0) && (
              <PerText fontSize="xxxl" className={valueClass}>
                {value}
              </PerText>
            )}
            <ValueComment comment={comment ? comment : ''} />
          </ValueRow>
        ) : (
          <NotPrepared />
        )}
      </Fragment>
    )
  }

  interface EmojiComponentProps {
    value?: number | null
    comment?: string | null
  }
  const EmojiComponent: FC<EmojiComponentProps> = ({ value, comment }) => {
    const dataE2eEmoji = ['sad', 'unhappy', 'indifferent', 'smirk', 'happy']
    const iconValues: (keyof typeof icons)[] = [
      'smileySad_fill',
      'smileyUnhappy_fill',
      'smileyIndifferent_fill',
      'smileySmirk_fill',
      'smileyHappy_fill',
    ]
    const colors = ['red', 'orange', 'yellow', 'mint', 'green']

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sharedInformation = meeting.appraisal!.selfReflectionShared

    return (
      <>
        {meeting.employeePrepared &&
        !sharedInformation &&
        (!meeting.managerPrepared || !meeting.selfReflectionAndEvaluationVisible) &&
        !userIsHR ? (
          <NotShared />
        ) : meeting.employeePrepared && (isDefined(value) || comment) ? (
          <>
            {value !== undefined && value !== null ? (
              <div className="fullHeight fullWidth" data-e2e-emoji={dataE2eEmoji[value]} key={'emoji-' + value}>
                <ValueRow justify className={'emoji-ratings--' + colors[value]}>
                  <ValueIcon name={iconValues[value]} />
                  <ValueComment comment={comment ? comment : ''} />
                </ValueRow>
              </div>
            ) : (
              <ValueRow justify>
                <ValueComment comment={comment ? comment : ''} />
              </ValueRow>
            )}
          </>
        ) : (
          <NotPrepared />
        )}
      </>
    )
  }

  const TrueFalseComponent: FC<{ value?: boolean | null; comment?: string | null }> = ({ value, comment }) => {
    const icon = value ? 'check_stroke' : 'close_stroke'
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sharedInformation = meeting.appraisal!.selfReflectionShared

    return (
      <>
        {meeting.employeePrepared &&
        !sharedInformation &&
        (!meeting.managerPrepared || !meeting.selfReflectionAndEvaluationVisible) &&
        !userIsHR ? (
          <NotShared />
        ) : meeting.employeePrepared && (isDefined(value) || comment) ? (
          <>
            <ValueRow>
              {isDefined(value) && <ValueIcon name={icon} color={'success'} />}
              <ValueComment comment={comment ? comment : ''} />
            </ValueRow>
          </>
        ) : (
          <NotPrepared />
        )}
      </>
    )
  }

  const renderCheckbox = (value: SelfreflectionQuestionBoolean[], comment?: string, index?: number) => {
    const hasChecked: boolean = value.filter((item) => !!item.value).length > 0
    const allUnchecked: boolean = value.filter((item) => !!item.value).length === 0
    const notPrepared = allUnchecked && !comment
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const sharedInformation = meeting.appraisal!.selfReflectionShared

    return (
      <Fragment key={'checkboxRow-' + index}>
        {meeting.employeePrepared &&
        !sharedInformation &&
        (!meeting.managerPrepared || !meeting.selfReflectionAndEvaluationVisible) &&
        !userIsHR ? (
          <NotShared />
        ) : meeting.employeePrepared && !notPrepared ? (
          <ValueRow>
            {hasChecked && (
              <PerFlexRow direction="column" className={valueClass} mr="m">
                {[...value]
                  .sort((a, b) => a.position - b.position)
                  .map(
                    (item, index) =>
                      item.value === true && (
                        <PerFlexRow mb="xxxs" key={'checkbox-' + index} noWrap>
                          <PerIcon name="check_stroke" color="primary" size={inlineIconSize} className="per-flexRow--fixed" />
                          <PerText fontWeight="bold" ml="xxs" className="per-flexRow--growShrink">
                            {t(`appraisalMeeting.selfReflection.booleans.${item.textId}`)}
                          </PerText>
                        </PerFlexRow>
                      ),
                  )}
              </PerFlexRow>
            )}
            <ValueComment comment={comment ? comment : ''} noMargin={allUnchecked} />
          </ValueRow>
        ) : (
          <NotPrepared />
        )}
      </Fragment>
    )
  }

  return (
    <>
      <div data-e2e-selfreflection-conclusion data-testid="selfreflection-wrapper">
        <PerFlexRow>
          <Tile header={t(`appraisalMeeting.selfReflection.questions.srQuestion1`)}>
            {selfReflectionQuestions
              .filter((question) => question.textId === 'srQuestion1')
              .map((question, index) => renderNumber(question.value, question.comment, index))}
          </Tile>
          <Tile header={t(`appraisalMeeting.selfReflection.questions.srQuestion2`)}>
            {selfReflectionQuestions
              .filter((question) => question.textId === 'srQuestion2')
              .map((question, index) => renderNumber(question.value, question.comment, index))}
          </Tile>
          <Tile header={t(`appraisalMeeting.selfReflection.questions.srQuestion3`)}>
            {selfReflectionQuestions
              .filter((question) => question.textId === 'srQuestion3')
              .map(({ value, comment }) => (
                <EmojiComponent key={`emoji-${value}`} value={value} comment={comment} />
              ))}
          </Tile>
        </PerFlexRow>

        <PerFlexRow>
          <Tile header={t(`appraisalMeeting.selfReflection.questions.srQuestion4`)}>
            {selfReflectionQuestions
              .filter((question) => question.textId === 'srQuestion4')
              .map((question, index) => (
                <TrueFalseComponent value={question.subItems[0].value} comment={question.comment} key={index} />
              ))}
          </Tile>
          <Tile header={t(`appraisalMeeting.selfReflection.questions.srQuestion5`)} wide={16}>
            {selfReflectionQuestions
              .filter((question) => question.textId === 'srQuestion5')
              .map((question, index) => renderCheckbox(question.subItems, question.comment as string | undefined, index))}
          </Tile>
        </PerFlexRow>
      </div>
    </>
  )
}
